import "./PanelMonitoreoOperativo.css";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { DarkModeContext } from "../../context/DarkModeContext";
import { Button, FormControl, InputGroup, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faCopy, faDownload, faEye, faFilter, faMagnifyingGlass, faRotateLeft } from "@fortawesome/free-solid-svg-icons";

const formatearMoneda = (valor) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(valor || 0);

const formatearPorcentaje = (valor) => `${Number(valor || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const obtenerClaseVariacion = (valor) => {
  if (valor <= -20) return "text-danger lato-bold";
  if (valor < 0) return "text-warning lato-bold";
  if (valor >= 20) return "text-success lato-bold";
  return "lato-bold";
};

const obtenerClaseEstado = (estado) => {
  if (!estado) return "monitoreo-badge monitoreo-badge-info";
  if (estado === "OK") return "monitoreo-badge monitoreo-badge-ok";
  if (estado === "CONTACTAR ALIADO") return "monitoreo-badge monitoreo-badge-danger";
  if (estado === "CAIDA VS PROMEDIO") return "monitoreo-badge monitoreo-badge-warning";
  return "monitoreo-badge monitoreo-badge-info";
};

const obtenerClaseCriticidad = (nivel) => {
  if (nivel === "Critica") return "monitoreo-criticality-chip monitoreo-criticality-chip-danger";
  if (nivel === "Alta") return "monitoreo-criticality-chip monitoreo-criticality-chip-warning";
  if (nivel === "Media") return "monitoreo-criticality-chip monitoreo-criticality-chip-neutral";
  if (nivel === "Oportunidad") return "monitoreo-criticality-chip monitoreo-criticality-chip-success";
  return "monitoreo-criticality-chip monitoreo-criticality-chip-neutral";
};

const PREFERENCIAS_DEFAULT = {
  mostrarResumen: true,
  mostrarAlertas: true,
  mostrarInsights: true,
};

const obtenerPreferencias = (key) => {
  try {
    const guardadas = localStorage.getItem(key);
    return guardadas ? { ...PREFERENCIAS_DEFAULT, ...JSON.parse(guardadas) } : PREFERENCIAS_DEFAULT;
  } catch (error) {
    return PREFERENCIAS_DEFAULT;
  }
};

const descargarCsv = (nombreArchivo, filas) => {
  const encabezados = Object.keys(filas[0] || {});
  const contenido = [
    encabezados.join(";"),
    ...filas.map((fila) =>
      encabezados
        .map((encabezado) => `"${String(fila[encabezado] ?? "").replace(/"/g, '""')}"`)
        .join(";")
    ),
  ].join("\n");

  const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nombreArchivo;
  link.click();
  URL.revokeObjectURL(url);
};

const TablaComerciosRiesgo = ({ items = [], data = null }) => {
  const { darkMode } = useContext(DarkModeContext);
  const [busqueda, setBusqueda] = useState("");
  const [filaExpandida, setFilaExpandida] = useState(null);
  const [soloAlertas, setSoloAlertas] = useState(false);
  const [preferencias, setPreferencias] = useState(() => obtenerPreferencias("panel-florencia-comercios-riesgo"));

  useEffect(() => {
    localStorage.setItem("panel-florencia-comercios-riesgo", JSON.stringify(preferencias));
  }, [preferencias]);

  const itemsBase = data?.items || items;

  const itemsFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    return itemsBase.filter((item) => {
      const coincideBusqueda = !termino || [item.cuit, item.cuitsVinculadosTexto, item.comercio, item.provincia, item.estado]
        .filter(Boolean)
        .some((valor) => valor.toLowerCase().includes(termino));
      const coincideAlerta = !soloAlertas || item.estado !== "OK";
      return coincideBusqueda && coincideAlerta;
    });
  }, [busqueda, itemsBase, soloAlertas]);

  const alertas = useMemo(() => itemsFiltrados.slice(0, 3), [itemsFiltrados]);

  const resumenVisual = useMemo(() => {
    const criticos = itemsFiltrados.filter((item) => item.estado === "CONTACTAR ALIADO").length;
    const conCaida = itemsFiltrados.filter((item) => item.estado === "CAIDA VS PROMEDIO").length;
    const conSeguimiento = itemsFiltrados.filter((item) => item.tieneSeguimientoActivo).length;

    return [
      {
        titulo: "Criticos",
        valor: criticos,
        detalle: criticos > 0 ? "Mayor prioridad comercial" : "Sin criticos filtrados",
      },
      {
        titulo: "Con caida",
        valor: conCaida,
        detalle: "Por debajo del promedio reciente",
      },
      {
        titulo: "Seguimiento activo",
        valor: conSeguimiento,
        detalle: "Ya tienen gestion en curso",
      },
    ];
  }, [itemsFiltrados]);

  const tablaClase = darkMode
    ? "monitoreo-operativo-tabla-dark"
    : "monitoreo-operativo-tabla";

  const alternarPreferencia = (clave) => {
    setPreferencias((prev) => ({ ...prev, [clave]: !prev[clave] }));
  };

  const exportar = () => {
    if (itemsFiltrados.length === 0) {
      return;
    }

    descargarCsv("comercios-riesgo.csv", itemsFiltrados.map((item) => ({
      Ranking: item.posicion,
      Estado: item.estado,
      Criticidad: item.nivelCriticidad,
      CUIT: item.cuit,
      CUITsVinculados: item.cuitsVinculadosTexto,
      Comercio: item.comercio,
      Provincia: item.provincia,
      VolumenHistorico: item.volumenHistorico,
      MontoHoy: item.montoDia,
      MontoAyer: item.montoAyer,
      Promedio3Dias: item.promedioTresDias,
      Promedio7Dias: item.promedioSieteDias,
      VariacionVsAyer: item.variacionVsAyer,
      VariacionVs7Dias: item.variacionVsSieteDias,
      TerminalesCaidaFuerte: item.terminalesConCaidaFuerte,
      TerminalesApagadas: item.terminalesApagadas,
      AccionSugerida: item.accionSugerida,
    })));
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto || "");
    } catch (error) {
      // noop
    }
  };

  return (
    <section className="container pb-4">
      <div className={`${tablaClase} p-4`}>
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4 monitoreo-section-head">
          <div className="monitoreo-table-title-block">
            <span className="monitoreo-kicker">Priorizacion comercial</span>
            <h4 className="lato-bold mb-1">Ranking de comercios en riesgo</h4>
            <span className="monitoreo-table-subtitle">Ordenado para decidir rápido qué comercio atender primero y por qué.</span>
          </div>
          <InputGroup style={{ maxWidth: "320px" }}>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
            <FormControl
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar por CUIT, comercio o provincia"
            />
          </InputGroup>
        </div>

        <div className="monitoreo-toolbar mb-4">
          <div className="d-flex flex-wrap gap-2">
            <Button className="monitoreo-toolbar-btn" variant="dark" size="sm" onClick={exportar}>
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Descargar reporte
            </Button>
            <Button
              className="monitoreo-toolbar-btn"
              variant={soloAlertas ? "dark" : "outline-dark"}
              size="sm"
              onClick={() => setSoloAlertas((prev) => !prev)}
            >
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              {soloAlertas ? "Mostrando alertas" : "Ver solo alertas"}
            </Button>
            <Button className="monitoreo-toolbar-btn" variant="outline-dark" size="sm" onClick={() => {
              setSoloAlertas(false);
              setBusqueda("");
            }}>
              <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
              Restablecer
            </Button>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button className="monitoreo-toolbar-btn" variant={preferencias.mostrarResumen ? "dark" : "outline-dark"} size="sm" onClick={() => alternarPreferencia("mostrarResumen")}>
              Resumen
            </Button>
            <Button className="monitoreo-toolbar-btn" variant={preferencias.mostrarAlertas ? "dark" : "outline-dark"} size="sm" onClick={() => alternarPreferencia("mostrarAlertas")}>
              Alertas
            </Button>
            <Button className="monitoreo-toolbar-btn" variant={preferencias.mostrarInsights ? "dark" : "outline-dark"} size="sm" onClick={() => alternarPreferencia("mostrarInsights")}>
              Insights
            </Button>
          </div>
        </div>

        {preferencias.mostrarInsights && data && (
          <div className={`monitoreo-general-panel ${darkMode ? "monitoreo-general-panel-dark" : ""}`}>
            <div className="row g-3">
              <div className="col-12 col-xl-5">
                <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Lectura general</div>
                <div className="lato-regular fs-15">{data.insightGeneral}</div>
              </div>
              <div className="col-12 col-xl-4">
                <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Alerta destacada</div>
                <div className="lato-bold fs-15">{data.alertaDestacada}</div>
              </div>
              <div className="col-12 col-xl-3">
                <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Recomendacion del dia</div>
                <div className="lato-regular fs-15">{data.accionGeneral}</div>
              </div>
            </div>
          </div>
        )}

        {preferencias.mostrarResumen && (
          <div className="row g-3 mb-4">
            {resumenVisual.map((card) => (
              <div className="col-12 col-lg-4" key={card.titulo}>
                <div className={`monitoreo-insight-card ${darkMode ? "monitoreo-insight-card-dark" : ""}`}>
                  <div className="lato-bold fs-12 text-uppercase color-gris">{card.titulo}</div>
                  <div className="lato-bold fs-28">{card.valor}</div>
                  <div className="lato-regular fs-14">{card.detalle}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {preferencias.mostrarAlertas && alertas.length > 0 && (
          <div className="mb-4">
            <div className="lato-bold fs-14 mb-2">Comercios para priorizar hoy</div>
            <div className="row g-3">
              {alertas.map((item) => (
                <div className="col-12 col-xl-4" key={`riesgo-${item.cuit}-${item.posicion}`}>
                  <div
                    className={`monitoreo-alert-card ${darkMode ? "monitoreo-alert-card-dark" : ""} ${
                      item.estado === "CONTACTAR ALIADO" ? "monitoreo-alert-card-danger" : "monitoreo-alert-card-warning"
                    }`}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <span className={obtenerClaseEstado(item.estado)}>{item.estado}</span>
                      <span className="lato-bold fs-12 color-gris">{item.nivelCriticidad}</span>
                    </div>
                    <div className="lato-bold fs-16 mb-1">{item.comercio}</div>
                    <div className="lato-regular fs-14 mb-2">{item.motivoPrincipal}</div>
                    <div className="lato-regular fs-13 color-gris">{item.accionSugerida}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="table-responsive">
          <Table hover className="align-middle mb-0 monitoreo-data-table">
            <thead>
              <tr>
                <th></th>
                <th>#</th>
                <th>Estado</th>
                <th>CUIT</th>
                <th>CUIT vinculados</th>
                <th>Comercio</th>
                <th>Provincia</th>
                <th>Volumen historico</th>
                <th>Monto hoy</th>
                <th>Ayer</th>
                <th>Promedio 3 dias</th>
                <th>Promedio 7 dias</th>
                <th>Terminales con caida fuerte</th>
                <th>Terminales apagadas</th>
                <th>Seguimiento</th>
              </tr>
            </thead>
            <tbody>
              {itemsFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={15} className="text-center py-4 lato-regular fs-16">
                    No hay comercios en riesgo para mostrar con los filtros actuales.
                  </td>
                </tr>
              ) : (
                itemsFiltrados.map((item) => {
                  const expandida = filaExpandida === item.cuit;
                  const terminalesAfectadas = (item.terminales || []).filter(
                    (terminal) => terminal.estado === "CONTACTAR ALIADO" || terminal.esCaidaFuerte
                  );

                  return (
                    <React.Fragment key={`${item.cuit}-${item.posicion}`}>
                      <tr>
                        <td>
                          <Button
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => setFilaExpandida(expandida ? null : item.cuit)}
                          >
                            <FontAwesomeIcon icon={expandida ? faChevronUp : faChevronDown} />
                          </Button>
                        </td>
                        <td className="lato-bold">{item.posicion}</td>
                        <td className="monitoreo-state-cell">
                          <div className="d-flex gap-1 align-items-center monitoreo-state-inline">
                            <span className={obtenerClaseEstado(item.estado)}>{item.estado}</span>
                            <span className={obtenerClaseCriticidad(item.nivelCriticidad)}>{item.nivelCriticidad}</span>
                          </div>
                        </td>
                        <td className="lato-bold monitoreo-cuit-cell">{item.cuit}</td>
                        <td className="monitoreo-linked-cell">{item.cuitsVinculados && item.cuitsVinculados.length > 1 ? item.cuitsVinculadosTexto : "-"}</td>
                        <td className="monitoreo-comercio-cell">
                          <div className="monitoreo-comercio-inline">
                            <div className="monitoreo-comercio-titulo">{item.comercio}</div>
                            {item.cuitsVinculados && item.cuitsVinculados.length > 1 ? (
                              <span className="monitoreo-meta-pill">Grupo de {item.cuitsVinculados.length} CUIT</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="monitoreo-plain-cell">{item.provincia}</td>
                        <td className="monitoreo-number-cell">{formatearMoneda(item.volumenHistorico)}</td>
                        <td className="monitoreo-number-cell">{formatearMoneda(item.montoDia)}</td>
                        <td className="monitoreo-number-cell">{formatearMoneda(item.montoAyer)}</td>
                        <td className="monitoreo-number-cell">{formatearMoneda(item.promedioTresDias)}</td>
                        <td className="monitoreo-number-cell">{formatearMoneda(item.promedioSieteDias)}</td>
                        <td className="monitoreo-number-cell">{item.terminalesConCaidaFuerte}</td>
                        <td className="monitoreo-number-cell">{item.terminalesApagadas}</td>
                        <td className="monitoreo-plain-cell">{item.tieneSeguimientoActivo ? "Activo" : "Pendiente"}</td>
                      </tr>
                      {expandida && (
                        <tr className="monitoreo-row-expandida">
                          <td colSpan={15}>
                            <div className="p-3 monitoreo-terminal-table">
                              <div className={`monitoreo-lectura-panel ${darkMode ? "monitoreo-lectura-panel-dark" : ""}`}>
                                <div className="monitoreo-detail-grid">
                                  <div className="monitoreo-detail-card">
                                    <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Motivo principal</div>
                                    <div className="lato-bold fs-16">{item.motivoPrincipal}</div>
                                  </div>
                                  <div className="monitoreo-detail-card">
                                    <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Insight</div>
                                    <div className="lato-regular fs-14">{item.insightPrincipal}</div>
                                  </div>
                                  <div className="monitoreo-detail-card">
                                    <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Accion sugerida</div>
                                    <div className="lato-regular fs-14">{item.accionSugerida}</div>
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex flex-wrap gap-2 mb-3">
                                <Button className="monitoreo-toolbar-btn" variant="outline-dark" size="sm" onClick={() => copiar(item.cuit)}>
                                  <FontAwesomeIcon icon={faCopy} className="me-2" />
                                  Copiar CUIT
                                </Button>
                                <Button className="monitoreo-toolbar-btn" variant="outline-dark" size="sm" onClick={() => setBusqueda(item.comercio || item.cuit)}>
                                  <FontAwesomeIcon icon={faEye} className="me-2" />
                                  Ver solo este comercio
                                </Button>
                              </div>

                              <div className="d-flex flex-wrap gap-4 mb-3">
                                <div>
                                  <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Volumen historico</div>
                                  <div className="lato-bold fs-18">{formatearMoneda(item.volumenHistorico)}</div>
                                </div>
                                <div>
                                  <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Ultima actividad</div>
                                  <div className="lato-bold fs-18">{item.ultimaActividad}</div>
                                </div>
                                <div>
                                  <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Dias sin facturar</div>
                                  <div className="lato-bold fs-18">{item.diasSinFacturar}</div>
                                </div>
                                <div>
                                  <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">CUIT del grupo</div>
                                  <div className="lato-bold fs-16">{item.cuitsVinculados && item.cuitsVinculados.length > 1 ? item.cuitsVinculadosTexto : item.cuit}</div>
                                </div>
                                <div>
                                  <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Variacion vs ayer</div>
                                  <div className={obtenerClaseVariacion(item.variacionVsAyer)}>{formatearPorcentaje(item.variacionVsAyer)}</div>
                                </div>
                                <div>
                                  <div className="monitoreo-metrica-label lato-bold fs-12 color-gris">Variacion vs 7 dias</div>
                                  <div className={obtenerClaseVariacion(item.variacionVsSieteDias)}>{formatearPorcentaje(item.variacionVsSieteDias)}</div>
                                </div>
                              </div>

                              <div className="table-responsive">
                                <Table bordered size="sm" className="mb-0 bg-white">
                                  <thead>
                                    <tr>
                                      <th>CUIT</th>
                                      <th>Terminal</th>
                                      <th>Comercio</th>
                                      <th>Estado</th>
                                      <th>Monto hoy</th>
                                      <th>Promedio 7 dias</th>
                                      <th>Variacion</th>
                                      <th>Dias sin facturar</th>
                                      <th>Ultima actividad</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {terminalesAfectadas.length === 0 ? (
                                      <tr>
                                        <td colSpan={8} className="text-center py-3">
                                          No hay terminales afectadas para este comercio.
                                        </td>
                                      </tr>
                                    ) : (
                                      terminalesAfectadas.map((terminal) => (
                                        <tr key={`${terminal.cuit}-${terminal.numeroTerminal}`}>
                                          <td className="lato-bold">{terminal.cuit}</td>
                                          <td className="lato-bold">{terminal.numeroTerminal}</td>
                                          <td>{terminal.nombreFantasia}</td>
                                          <td>
                                            <span className={obtenerClaseEstado(terminal.estado)}>
                                              {terminal.estado === "CAIDA VS PROMEDIO" && terminal.esCaidaFuerte
                                                ? "CAIDA FUERTE"
                                                : terminal.estado}
                                            </span>
                                          </td>
                                          <td>{formatearMoneda(terminal.montoDia)}</td>
                                          <td>{formatearMoneda(terminal.promedioSieteDias)}</td>
                                          <td className={obtenerClaseVariacion(terminal.variacionPorcentual)}>{formatearPorcentaje(terminal.variacionPorcentual)}</td>
                                          <td>{terminal.diasSinFacturar}</td>
                                          <td>{terminal.ultimaActividad}</td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </section>
  );
};

export default TablaComerciosRiesgo;
